import { useCallback } from 'react';
import type { BaseSchema, ValidationError } from 'yup';
import set from 'lodash/set';

// TODO: `any` type; FIXME
type ValidationResults =
  | {
      values: any;
      errors: any;
    }
  | {
      values: any;
      errors: any;
    };

type YupValidationResolver = (data: any) => Promise<ValidationResults>;

type ResolverFieldError = {
  message: string;
  type: string;
};

const normalizeYupPath = (path?: string): string => path?.replace(/\[(\d+)\]/g, '.$1').replace(/^\./, '') ?? 'root';

export function normalizeYupValidationErrors(validationErrors: ValidationError[]): Record<string, unknown> {
  const errorsByPath = new Map<string, ResolverFieldError>();
  validationErrors.forEach((error) => {
    const path = normalizeYupPath(error.path);
    errorsByPath.set(path, {
      message: error.message,
      type: error.type ?? 'validation',
    });
  });

  const paths = [...errorsByPath.keys()];
  const nestedErrors: Record<string, unknown> = {};
  [...errorsByPath.entries()]
    .sort(([leftPath], [rightPath]) => rightPath.split('.').length - leftPath.split('.').length)
    .forEach(([path, error]) => {
      const hasIndexedOrNestedError = paths.some((candidate) => candidate !== path && candidate.startsWith(`${path}.`));
      set(nestedErrors, hasIndexedOrNestedError ? `${path}.root` : path, error);
    });
  return nestedErrors;
}

export function useYupValidationResolver(validationSchema: BaseSchema): YupValidationResolver {
  return useCallback(
    async (data) => {
      try {
        const values = await validationSchema.validate(data, {
          abortEarly: false,
        });
        return {
          values,
          errors: {},
        };
      } catch (errors) {
        const validationErrors: ValidationError[] = errors.inner.length > 0 ? errors.inner : [errors];
        return {
          values: {},
          errors: normalizeYupValidationErrors(validationErrors),
        };
      }
    },
    [validationSchema]
  );
}
